import React, { useRef, useState } from 'react'
import { get, map, noop } from 'lodash'
import { rectsIntersect } from '@utils/trig.js'
import { createLinearScale, clamp } from '@utils/math'
import BackgroundContainer from '../../containers/BackgroundContainer'
import MarkerPositionIndicator from '../MarkerPositionIndicator'
import Marker from './Marker'
import Timespan from './Timespan'
import useFreescroll from '../../hooks/useFreescroll'
import FocusLayer from '../FocusLayer'
import ExpandedMarkerContent from './ExpandedMarkerContent'
import ExpandedTimespanContent from './ExpandedTimespanContent'
import type {
  Bounds,
  ScrollerData,
  TimelineDisplayBounds,
  Position,
  TimelineItemType,
  TimelineItemIdentifierObj,
} from '../../types/types.js'
import type { Marker as MarkerType, Timespan as TimespanType, Photo } from '@app-types/entity_types'
import type { DragEventData } from '../../types/gesture-types.js'
import { useHorizontalTimelineContext } from '../../context'

type DisplayableTimelineItems = {
  topY: number
  markers: Record<string, Bounds>
  timespans: Record<string, Bounds>
}

type TimelineProps = {
  markers: MarkerType[]
  timespans: TimespanType[]
  photos: Record<number, Photo>
  displayableTimelineItems: DisplayableTimelineItems
  drilledDownTimelineItem: TimelineItemIdentifierObj | null
  expandedItemBounds: Bounds
  stageWidth: number
  stageHeight: number

  scrollerData: ScrollerData
  timelineDisplayBounds: TimelineDisplayBounds
  onPanStart: () => void
  onPanMove: (nextLeft: number, nextRight: number) => void
  onPanEnd: () => void
  onTimelineItemDrillDown: (id: number | string, timelineItemType: TimelineItemType) => void
  onTimelineItemDrillUp: (id: number | string, timelineItemType: TimelineItemType) => void
}

type FocusedItemState =
  | (TimelineItemIdentifierObj & {
      initialBounds: Bounds | null
      itemData: MarkerType | TimespanType
    })
  | null

type LinearScale = {
  (value: number): number
  domain(value: [number, number]): void
  range(value: [number, number]): void
}

type YScaleArg = LinearScale | null

const Timeline: React.FC<TimelineProps> = props => {
  const {
    markers,
    timespans,
    photos,
    displayableTimelineItems,
    expandedItemBounds,
    stageWidth,
    stageHeight,
    scrollerData,
    timelineDisplayBounds,
    onPanStart = noop,
    onPanEnd = noop,
    onTimelineItemDrillDown = noop,
    onTimelineItemDrillUp = noop,
  } = props

  const { state, dispatch } = useHorizontalTimelineContext()
  const { freeScrolling } = state

  const [focusedItem, setFocusedItem] = useState<FocusedItemState>(null)
  const timelineContainerRef = useRef<HTMLDivElement>(null)

  const { handleFreescrollPanStart, handleFreescrollPanMove, handleFreescrollPanEnd } = useFreescroll({
    scrollerData,
    onPanMove: props.onPanMove,
    dispatch,
    freeScrolling,
  })

  const localPrevPositionRef = useRef<Position | null>(null)

  const handlePanStart = (eventData: DragEventData) => {
    handleFreescrollPanStart()

    const center = {
      x: eventData.event?.clientX || 0,
      y: eventData.event?.clientY || 0,
    }
    localPrevPositionRef.current = center
    onPanStart()
  }

  const handlePanMove = (eventData: DragEventData) => {
    const center = {
      x: eventData.event?.clientX || 0,
      y: eventData.event?.clientY || 0,
    }
    const timeStamp = eventData.event?.timeStamp || performance.now()

    handleFreescrollPanMove(center, timeStamp)

    const prevCenter = localPrevPositionRef.current
    localPrevPositionRef.current = center
    if (prevCenter) {
      const movementX = (prevCenter.x - center.x) * -1
      timelineDrag(movementX)
    }
  }

  const handlePanEnd = (_eventData: DragEventData) => {
    handleFreescrollPanEnd()
    onPanEnd()
  }

  // onTap callback for Marker and Timespan components
  const handleTimelineItemTap = (
    id: number | string,
    type: TimelineItemType,
    itemBoundsOnViewport: Bounds // These are viewport-relative bounds from getBoundingClientRect()
  ) => {
    let timelineContainerViewportRect = { left: 0, top: 0 }
    if (timelineContainerRef.current) {
      const rect = timelineContainerRef.current.getBoundingClientRect()
      timelineContainerViewportRect = { left: rect.left, top: rect.top }
    }

    let itemData: MarkerType | TimespanType | undefined
    // The initialBounds for FocusLayer need to be relative to the timeline container,
    // not the viewport. So we adjust itemBoundsOnViewport by the timeline container's offset.
    const initialBoundsRelativeToTimeline: Bounds = {
      x: itemBoundsOnViewport.x - timelineContainerViewportRect.left,
      y: itemBoundsOnViewport.y - timelineContainerViewportRect.top,
      width: itemBoundsOnViewport.width,
      height: itemBoundsOnViewport.height,
    }

    if (type === 'marker') {
      itemData = markers.find(m => m.id === id)
    } else if (type === 'timespan') {
      itemData = timespans.find(t => t.id === id)
    }

    if (itemData) {
      setFocusedItem({
        id,
        timelineItemType: type,
        initialBounds: initialBoundsRelativeToTimeline,
        itemData,
      })
      onTimelineItemDrillDown(id, type)
    }
  }

  const handleFocusLayerClose = () => {
    if (focusedItem) {
      onTimelineItemDrillUp(focusedItem.id, focusedItem.timelineItemType)
      setFocusedItem(null)
    }
  }

  const timelineDrag = (movementX: number) => {
    const currentScrollerData = scrollerData
    const viewportWidth = currentScrollerData.viewport.width
    const surfaceX = currentScrollerData.surface.x
    const surfaceWidth = currentScrollerData.surface.width

    const minX = 0 + viewportWidth - surfaceWidth
    const maxX = 0
    const nextSurfaceX = clamp(surfaceX + movementX, minX, maxX)

    const newViewportLeft = Math.abs(nextSurfaceX)
    const newViewportRight = newViewportLeft + viewportWidth

    const scale = createLinearScale([0, surfaceWidth], [0, 1])
    const nextLeft = scale(newViewportLeft)
    const nextRight = scale(newViewportRight)

    props.onPanMove(nextLeft, nextRight)
  }

  const isOnStage = (itemDimensions: Bounds) => {
    const viewportDimensions = timelineDisplayBounds.viewport
    const bounds = {
      x: viewportDimensions.x,
      y: viewportDimensions.y,
      width: viewportDimensions.width,
      height: viewportDimensions.height,
    }
    return rectsIntersect(bounds, itemDimensions)
  }

  const getBackgroundContent = () => {
    return (
      <BackgroundContainer
        markers={markers}
        timespans={timespans}
        onPanStart={handlePanStart}
        onPanMove={handlePanMove}
        onPanEnd={handlePanEnd}
      />
    )
  }

  const getMarkerItemsContent = (offsetVal: number = 0, yScaleVal: YScaleArg = null) => {
    if (markers != null) {
      return map(markers, marker => {
        const markerId = marker.id.toString() // Ensure ID is string for `get`
        const item: Bounds = get(displayableTimelineItems.markers, markerId)
        if (!item) return null

        const x = item.x + offsetVal
        const y = yScaleVal === null ? item.y : yScaleVal(item.y)
        const width = item.width
        const height = item.height
        const zIndex = 20

        const visible = isOnStage({ x, y, width, height })
        if (!visible) {
          return null
        }

        let positionIndicator = null
        const lineBottomMargin = 8
        const lineY = y + height
        const axisMargin = 50
        const indicatorData = {
          x: x + 1,
          y: lineY,
          height: timelineDisplayBounds.viewport.height + axisMargin - lineY - lineBottomMargin,
        }
        positionIndicator = (
          <MarkerPositionIndicator
            key={`marker-${markerId}-positionIndicator`}
            x={indicatorData.x}
            y={indicatorData.y}
            height={indicatorData.height}
          />
        )

        let photoData = null
        if (marker.photo_ids.length > 0) {
          const photoId = marker.photo_ids[0].toString()
          // Assuming photos is an object keyed by id, or an array findable by id
          // If photos is an array: photos.find(p => p.id === photoId)
          // If photos is an object: get(photos, photoId)
          const foundPhoto = Array.isArray(photos)
            ? photos.find(p => p.id.toString() === photoId)
            : get(photos, photoId)
          photoData = foundPhoto || null
        }

        return (
          <div key={`marker-${markerId}`}>
            <Marker
              marker={marker}
              photoData={photoData}
              x={x}
              y={y}
              width={width}
              height={height}
              zIndex={zIndex}
              onTap={handleTimelineItemTap}
              onPanStart={handlePanStart}
              onPanMove={handlePanMove}
              onPanEnd={handlePanEnd}
            />
            {positionIndicator}
          </div>
        )
      })
    }
    return null
  }

  const getTimespanItemsContent = (offsetVal: number = 0, yScaleVal: YScaleArg = null) => {
    if (timespans != null) {
      return map(timespans, (timespan: TimespanType) => {
        const timespanId = timespan.id.toString()
        const item: Bounds = get(displayableTimelineItems.timespans, timespanId)
        if (!item) return null
        const x = item.x + offsetVal
        const y = yScaleVal === null ? item.y : yScaleVal(item.y)
        const width = item.width
        const height = item.height
        const zIndex = 20

        const visible = isOnStage({ x, y, width, height })
        if (!visible) {
          return null
        }

        return (
          <div key={`timespan-${timespanId}`}>
            <Timespan
              timespan={timespan}
              x={x}
              y={y}
              width={width}
              height={height}
              zIndex={zIndex}
              onTap={handleTimelineItemTap} // Timespan will call this with its id, type, bounds, and color
              onPanStart={handlePanStart}
              onPanMove={handlePanMove}
              onPanEnd={handlePanEnd}
            />
          </div>
        )
      })
    }
    return null
  }

  const renderFocusedItemContent = () => {
    if (!focusedItem || !focusedItem.itemData) return null

    const { timelineItemType, itemData } = focusedItem

    if (timelineItemType === 'marker') {
      return (
        <ExpandedMarkerContent
          marker={itemData as MarkerType}
          photos={photos}
          expandedItemBounds={expandedItemBounds}
        />
      )
    } else if (timelineItemType === 'timespan') {
      return <ExpandedTimespanContent timespan={itemData as TimespanType} expandedItemBounds={expandedItemBounds} />
    }
    return null
  }

  const offset = timelineDisplayBounds.surface.x
  const surfaceHeight = timelineDisplayBounds.surface.height
  const topY = displayableTimelineItems.topY
  let yScale: YScaleArg = null
  if (topY < 0) {
    yScale = createLinearScale([topY, surfaceHeight], [5, surfaceHeight - 5])
  }

  return (
    <div
      ref={timelineContainerRef}
      className="html-timeline"
      style={{
        width: stageWidth,
        height: stageHeight,
      }}
    >
      {getBackgroundContent()}
      <div
        className="timeline-items"
        style={{
          width: timelineDisplayBounds.surface.width,
          height: stageHeight, // Or the actual height of the scrollable surface
        }}
      >
        {getMarkerItemsContent(offset, yScale)}
        {getTimespanItemsContent(offset, yScale)}
      </div>

      <FocusLayer
        isOpen={!!focusedItem}
        initialBounds={focusedItem?.initialBounds || null}
        finalBounds={expandedItemBounds}
        onClose={handleFocusLayerClose}
        stageWidth={stageWidth}
        stageHeight={stageHeight}
      >
        {focusedItem && renderFocusedItemContent()}
      </FocusLayer>
      <div
        id="js-timeline-overlay"
        className="timeline-overlay"
        style={{
          maxWidth: stageWidth,
          maxHeight: stageHeight,
        }}
      ></div>
    </div>
  )
}

export default Timeline
